import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button, Row, Col } from "reactstrap";
import {
  Table,
  Tag,
  Space,
  Modal,
  message,
  Card,
  Descriptions,
  Divider,
  Alert,
} from "antd";
import {
  CheckCircleOutlined,
  PrinterOutlined,
  EditOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Icon } from "../../../components/Component";
import { WorkRequestFormData, WorkRequestLine, WorkLineSource } from "../types";
import {
  createWorkRequest,
  updateWorkRequest,
} from "../mockData/workRequestData";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";

const WorkRequestPreview: React.FC = (props: any) => {
  const { handleSubmit, watch } = useFormContext<WorkRequestFormData>();
  const [submitting, setSubmitting] = useState(false);
  const Navigate = useNavigate();
  // Watch all form values
  const formData = watch();
  const workRequestLines: WorkRequestLine[] = formData.workRequestLines || [];

  const getRequestForDisplay = (value: string) => {
    return value; // Already in display format from enum
  };

  const getApprovalDisplay = (value: string) => {
    return value === "true" ? "Yes" : "No";
  };

  const formatDate = (date: any) => {
    if (!date) return "-";
    return dayjs(date).format("MM/DD/YYYY");
  };

  const getSourceColor = (source: WorkLineSource) => {
    const colors = {
      [WorkLineSource.ProjectManagement]: "blue",
      [WorkLineSource.WorkBOQ]: "green",
      [WorkLineSource.NewWorkLine]: "orange",
    };
    return colors[source] || "default";
  };

  const getSourceDisplayName = (source: string) => {
    switch (source) {
      case "Project Management":
        return "Project Mgmt";
      case "Work BOQ":
        return "Work BOQ";
      case "New Work Line":
        return "New Work";
      default:
        return source;
    }
  };

  // Material BOQ expandable row component for preview
  const expandedRowRender = (record: WorkRequestLine) => {
    const materialAllocation = (record as any).materialAllocation;
    
    if (!materialAllocation || materialAllocation.length === 0) {
      return null;
    }

    const materialColumns = [
      {
        title: 'Material Name',
        dataIndex: 'materialName',
        key: 'materialName',
        width: '40%',
        render: (name: string, material: any) => (
          <div>
            <div className="fw-bold">{name}</div>
            {material.category && (
              <Tag color="blue" style={{ marginTop: 4 }}>{material.category}</Tag>
            )}
          </div>
        ),
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        width: '20%',
        align: 'right' as const,
        render: (qty: number) => <strong>{qty.toLocaleString()}</strong>,
      },
      {
        title: 'UOM',
        dataIndex: 'uom',
        key: 'uom',
        width: '20%',
        align: 'center' as const,
        render: (uom: string) => <Tag color="geekblue">{uom}</Tag>,
      },
      {
        title: 'Material ID',
        dataIndex: 'materialId',
        key: 'materialId',
        width: '20%',
        render: (id: string) => <code className="text-muted">{id}</code>,
      },
    ];

    return (
      <div style={{ margin: '16px 0', backgroundColor: '#fafafa', padding: '16px', borderRadius: '6px' }}>
        <div className="mb-2">
          <strong>📦 Material BOQ Allocation ({materialAllocation.length} items)</strong>
        </div>
        <Table
          columns={materialColumns}
          dataSource={materialAllocation}
          pagination={false}
          size="small"
          rowKey="id"
          bordered
        />
      </div>
    );
  };

  // FSD Preview - Work Request Lines Table Format
  const workLinesColumns = [
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 150,
      render: (text: string, record: WorkRequestLine) => (
        <div>
          <div className="fw-bold">{text}</div>
          {record.taskDescription && (
            <small className="text-muted">{record.taskDescription}</small>
          )}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 100,
      render: (text: string, record: WorkRequestLine) => (
        <div>
          <div>{text}</div>
          {record.subcategory && (
            <small className="text-muted">{record.subcategory}</small>
          )}
        </div>
      ),
    },
    {
      title: "Subcategory",
      dataIndex: "subcategory",
      key: "subcategory",
      width: 100,
      render: (text: string) => text || "-",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 80,
      align: "right" as const,
      render: (quantity: number) => (
        <strong>{quantity.toLocaleString()}</strong>
      ),
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 60,
      align: "center" as const,
    },
    {
      title: "From Source",
      dataIndex: "source",
      key: "source",
      width: 100,
      render: (source: string, record: WorkRequestLine) => {
        const materialAllocation = (record as any).materialAllocation;
        const hasMaterialBOQ = materialAllocation && materialAllocation.length > 0;
        
        return (
          <div>
            <Tag color={getSourceColor(source as WorkLineSource)}>
              {getSourceDisplayName(source)}
            </Tag>
            {hasMaterialBOQ && (
              <div style={{ marginTop: 4 }}>
                <Tag color="purple" style={{ fontSize: '10px' }}>📦 Material BOQ</Tag>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Assigned Tower",
      key: "assignedTower",
      width: 100,
      render: (_: any, record: WorkRequestLine) => {
        if (record.assignedTower) return record.assignedTower;
        if (record.assignedVilla) return record.assignedVilla;
        return "-";
      },
    },
    {
      title: "Start Date",
      dataIndex: "plannedStartDate",
      key: "startDate",
      width: 100,
      render: (date: string) => date || "-",
    },
    {
      title: "End Date",
      dataIndex: "plannedEndDate",
      key: "endDate",
      width: 100,
      render: (date: string) => date || "-",
    },
  ];

  const validateFinalSubmission = (data: WorkRequestFormData) => {
    // Final validation before submission
    if (workRequestLines.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please add at least one work request line",
      });
      return false;
    }

    if (!data.workRequestTitle?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Request Title is required",
      });
      return false;
    }

    if (!data.siteId) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Site selection is required",
      });
      return false;
    }

    return true;
  };

  const submitForm = async (data: WorkRequestFormData) => {
    if (!validateFinalSubmission(data)) return;

    try {
      setSubmitting(true);

      // Prepare submission data
      const submissionData = {
        ...data,
        workRequestLines,
        plannedStartDate: formatDate(data.plannedStartDate),
        plannedEndDate: formatDate(data.plannedEndDate),
        approvalRequired: data.approvalRequired === "true",
        createdBy: "Current User", // Would come from auth context
        createdAt: new Date().toISOString(),
      };

      let result;
      if (data.type === "edit") {
        // Update existing work request
        const workRequestId = data.workRequestNumber; // Assuming we have ID logic
        result = updateWorkRequest(workRequestId, submissionData);
      } else {
        // Create new work request
        result = createWorkRequest(submissionData);
      }

      if (result) {
        // Show success modal with FSD output information
        Swal.fire({
          title: "Work Request Submitted Successfully!",
          // html: `
          //   <div style="text-align: left;">
          //     <p><strong>Work Request #:</strong> ${
          //       result.workRequestNumber
          //     }</p>
          //     <p><strong>Title:</strong> ${result.workRequestTitle}</p>
          //     <p><strong>Status:</strong> ${result.status}</p>
          //     <p><strong>Total Work Lines:</strong> ${result.totalLines}</p>
          //     ${
          //       result.approvalRequired
          //         ? "<p><strong>Next Step:</strong> Pending Approval</p>"
          //         : "<p><strong>Next Step:</strong> Ready for Work Order generation</p>"
          //     }
          //   </div>
          // `,
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "Go to Work Request List",
          cancelButtonText: "Create Another",
          confirmButtonColor: "#28a745",
          cancelButtonColor: "#6c757d",
        }).then((result) => {
          if (result.isConfirmed) {
            // Navigate to work request list
            Navigate("/work-request-list");
            window.location.href = "/work-request-list";
          } else {
            // Navigate to create new work request
            window.location.href = "/work-request-creation";
          }
        });
      } else {
        throw new Error("Failed to save work request");
      }
    } catch (error) {
      console.log("Error submitting work request:", error);
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: "Failed to submit work request. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="content clearfix">
        <div className="card">
          <div className="card-body">
            {/* <div className="card-title">
              <h5>📃 3. Preview Section</h5>
              <p className="text-muted">
                Summarized view of all General Information and Selected Work
                Request Lines
              </p>
            </div> */}

            {/* FSD Preview - Sample Data -- General Information */}
            <Card title="📄 General Information" className="mb-4">
              <Descriptions
                bordered
                column={2}
                size="small"
                labelStyle={{ fontWeight: "bold", width: "200px" }}
              >
                <Descriptions.Item label="Work Request #" span={1}>
                  <strong>
                    {formData.workRequestNumber || "Auto-generated"}
                  </strong>
                </Descriptions.Item>
                <Descriptions.Item label="Work Request Title" span={1}>
                  <strong>{formData.workRequestTitle || "-"}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Description" span={2}>
                  {formData.description || "No description provided"}
                </Descriptions.Item>
                <Descriptions.Item label="Site Selection" span={1}>
                  <strong>{formData.siteName || formData.siteId || "-"}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Project Selection" span={1}>
                  {formData.projectName || formData.projectId || "Not selected"}
                </Descriptions.Item>
                <Descriptions.Item label="Request For" span={1}>
                  <Tag color="blue">
                    {getRequestForDisplay(formData.requestFor) || "-"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Approval Required" span={1}>
                  <Tag
                    color={
                      formData.approvalRequired === "true" ? "orange" : "green"
                    }
                  >
                    {getApprovalDisplay(formData.approvalRequired)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Planned Start Date" span={1}>
                  <strong>{formatDate(formData.plannedStartDate)}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Planned End Date" span={1}>
                  <strong>{formatDate(formData.plannedEndDate)}</strong>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* FSD Preview - Sample - Work Request Lines */}
            <Card
              title={`📄 Work Request Lines (${workRequestLines.length} lines)`}
              className="mb-4"
            >
              {workRequestLines.length > 0 ? (
                <Table
                  columns={workLinesColumns}
                  dataSource={workRequestLines}
                  pagination={false}
                  scroll={{ x: 1000 }}
                  size="small"
                  rowKey="id"
                  expandable={{
                    expandedRowRender,
                    expandIcon: ({ expanded, onExpand, record }) => {
                      const materialAllocation = (record as any).materialAllocation;
                      const hasMaterialBOQ = materialAllocation && materialAllocation.length > 0;
                      
                      if (!hasMaterialBOQ) return null;
                      
                      return expanded ? (
                        <DownOutlined 
                          onClick={e => onExpand(record, e)} 
                          style={{ color: '#1890ff', cursor: 'pointer' }}
                        />
                      ) : (
                        <RightOutlined 
                          onClick={e => onExpand(record, e)} 
                          style={{ color: '#1890ff', cursor: 'pointer' }}
                        />
                      );
                    },
                    rowExpandable: (record) => {
                      const materialAllocation = (record as any).materialAllocation;
                      return materialAllocation && materialAllocation.length > 0;
                    },
                  }}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={9}>
                        <div className="text-end">
                          <strong>
                            Total Work Lines: {workRequestLines.length}
                          </strong>
                        </div>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              ) : (
                <Alert
                  message="No Work Lines Added"
                  description="Please go back to Work Request Details section and add at least one work line."
                  type="warning"
                  showIcon
                />
              )}
            </Card>
          </div>
        </div>

        <div className="actions clearfix">
          <ul>
            <li>
              <Space>
                <Button
                  color="primary"
                  type="button"
                  onClick={handleSubmit(submitForm)}
                  loading={submitting}
                  disabled={workRequestLines.length === 0}
                >
                  <CheckCircleOutlined className="me-1" />
                  {formData.approvalRequired === "true"
                    ? "Submit for Approval"
                    : "Save Work Request"}
                </Button>
              </Space>
            </li>
            <li>
              <Button color="light" type="button" onClick={props.prev}>
                <Icon name="arrow-left" className="me-1" />
                Previous
              </Button>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default WorkRequestPreview;
